import { ReactNode } from "react";
import { Box, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText";
import { formatPrice, formatQuantity } from "@common/utils/string";
import UsageGraph from "@components/ui/usage/UsageGraph";
import { UsageListing } from "@addons/components/MeteredAddOnUsage";
import { TrialConversionModal } from "@addons/components/TrialConversionModal";
import {
  IAddOnSlotProps,
  IMeteredAddOnDescriptor,
  IMeteredAddOnUsage,
} from "../../types";

interface Props extends IAddOnSlotProps<IMeteredAddOnDescriptor> {
  /** the advanced upsell, which only the basic tier shows */
  upsell?: ReactNode;
}

export function TransformsUsagePanel({
  addOn: { isTrial, getUsage, getPricePerUnit },
  upsell,
}: Props) {
  const usage = getUsage();

  return (
    <Stack data-testid="ManageTransformsPanel" spacing="lg">
      {!usage?.areFreeUnitsConsumed ? (
        <IncludedUsage usage={usage}>
          <Text data-testid="TransformsUsageCopy" size="sm" mt="xs">
            We&apos;ll notify you when you&apos;ve hit 80% of your included
            usage
          </Text>
        </IncludedUsage>
      ) : isTrial ? (
        <IncludedUsage usage={usage}>
          <ConvertTrialEarly />
        </IncludedUsage>
      ) : (
        <BilledRuns usage={usage} pricePerRun={getPricePerUnit()} />
      )}

      {upsell}
    </Stack>
  );
}

function IncludedUsage({
  usage,
  children,
}: {
  usage: IMeteredAddOnUsage | undefined;
  children: ReactNode;
}) {
  return (
    <Box>
      <UsageGraph
        data-testid="TransformsUsageGraph"
        hasQuota={!!usage?.hasQuota}
        usage={usage?.includedUnitsUsed ?? 0}
        prepaidUnits={usage?.includedUnits ?? 0}
        metricLabel="Included use"
        maxLabel={
          usage?.includedUnits ? formatQuantity(usage.includedUnits) : null
        }
      />
      {children}
    </Box>
  );
}

function BilledRuns({
  usage,
  pricePerRun,
}: {
  usage: IMeteredAddOnUsage;
  pricePerRun: number;
}) {
  const billableRuns = usage.billableUnits;

  return (
    <UsageListing
      dataTestId="TransformsUsageListing"
      periodLabel="Current cycle"
      rowLabel={`Runs (${formatPrice(pricePerRun)})`}
      value={billableRuns}
    >
      <Text fw={700} fz="sm" ta="right" w="100%">
        Cost: {formatPrice(billableRuns * pricePerRun)}
      </Text>
    </UsageListing>
  );
}

function ConvertTrialEarly() {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <Text size="sm" mt="xs">
        Wow, you really love transforms! You&apos;ve used your entire included
        allotment during your trial. In order for us to keep your transforms
        running, we need you to{" "}
        <ButtonAsText
          data-testid="TransformsConversionCopy"
          component="button"
          fw={700}
          onClick={open}
        >
          convert your trial early
        </ButtonAsText>{" "}
        so we can bill you for additional runs.
      </Text>

      <TrialConversionModal opened={opened} onClose={close} />
    </>
  );
}
